import { useState } from 'react';
import { selectTenantPrompt } from '../../copy';
import { useOne, useUpdate } from '@refinedev/core';
import { Spin, Alert, Empty, message } from 'antd';
import { useParams } from 'react-router';
import { useCompanyScope } from '../../components/CompanyScopePicker';
import { RequireCan } from '../../components/RequireCan';
import { QuestionForm } from './QuestionForm';
import type { DiscoveryQuestion, QuestionPayload } from './types';

/**
 * Edit an inventory question as a BARE form — no Refine `<Edit>` page chrome
 * — so it can render in place inside the shared `DetailPage` (see
 * `QuestionDetail.tsx`). Unlike `CompanyEdit`/`IntegrationEdit`/`ToolkitsEdit`,
 * this does NOT use `@refinedev/antd`'s `useForm` — `QuestionForm` is a
 * hand-rolled stateful component (not bound to an antd `<Form>`), so the
 * fetch/mutate wiring stays the plain `useOne`/`useUpdate` this page already
 * used before the refactor; only the page chrome and the callback contract
 * change.
 *
 * The tenant comes from the global scope: `getOne` auto-appends
 * `?companyId=` for the scope-aware `questions` resource (the dataProvider's
 * `scopeCompanyIdFor` fallback), but `update` does NOT — it only honors an
 * EXPLICIT `meta.companyId`, so the mutation threads it directly.
 *
 * `onSaved` reports success so `QuestionDetail` flips back to read mode;
 * `onDirtyChange` is forwarded straight from `QuestionForm` (see that file's
 * "touched" tracking — there's no antd `onValuesChange` to hook here).
 * `hideCancel` suppresses `QuestionForm`'s own Cancel button: the shared
 * `DetailPage` already renders one Cancel in its header, routed through its
 * discard guard.
 *
 * All props are optional so a caller can still render `<QuestionEdit/>` bare.
 * `id` is passed explicitly for robustness (at /questions/:id the inferred
 * Refine action is `show`, not `edit`); it falls back to the route param.
 */
export interface QuestionEditProps {
  id?: string;
  onSaved?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

export function QuestionEdit({ id, onSaved, onDirtyChange }: QuestionEditProps = {}) {
  const params = useParams<{ id: string }>();
  const recordId = id ?? params.id;
  const { companyId } = useCompanyScope();
  const { mutate } = useUpdate();
  const [saving, setSaving] = useState(false);

  const { result: record, query } = useOne<DiscoveryQuestion>({
    resource: 'questions',
    id: recordId,
    queryOptions: { enabled: companyId != null && !!recordId },
  });

  function onSubmit(payload: QuestionPayload) {
    if (!recordId) return;
    setSaving(true);
    mutate(
      { resource: 'questions', id: recordId, values: payload, meta: { companyId } },
      {
        onSuccess: () => {
          message.success('Question saved');
          onSaved?.();
        },
        onError: (err) =>
          message.error((err as { message?: string }).message ?? 'Save failed'),
        onSettled: () => setSaving(false),
      },
    );
  }

  return (
    <RequireCan I="manage" a="DiscoveryQuestion">
      {(() => {
        if (!companyId) {
          return (
            <Empty
              description={selectTenantPrompt('manage its Questions Inventory')}
              style={{ padding: '48px 0' }}
            />
          );
        }
        if (query?.isError) {
          return (
            <Alert
              type="error"
              message="Couldn't load question"
              description={(query.error as { message?: string })?.message ?? 'Failed to load'}
            />
          );
        }
        if (query?.isLoading || !record) return <Spin />;

        return (
          <QuestionForm
            initial={record}
            showDefault
            submitting={saving}
            onSubmit={onSubmit}
            onCancel={() => undefined}
            hideCancel
            onDirtyChange={onDirtyChange}
          />
        );
      })()}
    </RequireCan>
  );
}
